import { useState } from "react";

import { Button } from "common/components/ui/Button";
import { DrawerClose } from "common/components/ui/Drawer";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import { PopoverClose } from "common/components/ui/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
} from "common/components/ui/Select";
import Switch from "common/components/ui/Switch";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  CrmExportType,
  InstalledIntegrationSettingsItem,
} from "common/types";

import useIntegrationMutations from "../../datahooks/useIntegrationMutations";
import IntegrationSettingsError from "./IntegrationSettingsError";
import RemoveIntegrationButton from "./RemoveIntegrationButton";
import SettingsContainer from "./SettingsContainer";

const options = [
  { value: "new_connection", label: "All leads" },
  { value: "accepted", label: "Aimfox campaign leads" },
] as const;

type CrmIntegrationSettingsProps = {
  crmExportType: CrmExportType;
} & ({ settings: InstalledIntegrationSettingsItem } | { errorMessage: string });

export default function CrmIntegrationSettings({
  crmExportType,
  ...rest
}: CrmIntegrationSettingsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [isOpen, setIsOpen] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(
    "settings" in rest ? rest.settings : null,
  );

  const { updateCrmIntegration, isUpdatingCrmIntegration } =
    useIntegrationMutations();

  function onSave() {
    updateCrmIntegration({ updates: currentSettings, crmExportType }).then(
      () => {
        setIsOpen(false);
      },
    );
  }

  const Close = isDesktop ? PopoverClose : DrawerClose;

  const footerContent = (
    <>
      <Close asChild>
        <Button
          variant="secondary-black"
          size="md"
          disabled={isUpdatingCrmIntegration}
        >
          Cancel
        </Button>
      </Close>
      <Button size="md" isLoading={isUpdatingCrmIntegration} onClick={onSave}>
        Save
      </Button>
    </>
  );

  return (
    <SettingsContainer
      integrationKey={crmExportType}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      footerContent={"settings" in rest ? footerContent : null}
    >
      {"errorMessage" in rest ? (
        <IntegrationSettingsError errorMessage={rest.errorMessage} />
      ) : (
        <>
          <div className="bg-black-50 rounded-2xl p-2 flex gap-x-2.5">
            <div className="flex flex-col gap-y-0.5 grow">
              <span className="text-sm-bold">Automatic sync</span>
              <p className="text-black-500 text-xs-medium">
                Automatically send Aimfox lead data to HubSpot
              </p>
            </div>
            <Switch
              checked={currentSettings.active}
              onCheckedChange={() => {
                setCurrentSettings({
                  ...currentSettings,
                  active: !currentSettings.active,
                });
              }}
            />
          </div>
          <FormField label="Which leads to sync" htmlFor="" size="md">
            <Select
              value={currentSettings.events[0]}
              onValueChange={(value) =>
                setCurrentSettings({
                  ...currentSettings,
                  events: [value as (typeof options)[number]["value"]],
                })
              }
            >
              <SelectTrigger asChild>
                <FilterButton size="md">
                  {
                    options.find(
                      ({ value }) => value === currentSettings.events[0],
                    ).label
                  }
                </FilterButton>
              </SelectTrigger>
              <SelectContent>
                {options.map(({ value, label }) => (
                  <SelectItem value={value} key={value}>
                    <SelectItemText>{label}</SelectItemText>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </>
      )}
      <RemoveIntegrationButton integrationKey={crmExportType} />
    </SettingsContainer>
  );
}
